import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import useDeleteData from "../../../../hooks/useDeleteData";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import Create from "./Create";
import Edit from "./Edit";

const List = () => {
  const location = useLocation();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [currentStory, setCurrentStory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [projects, setProjects] = useState({});
  const [labels, setLabels] = useState({});
  const [developers, setDevelopers] = useState({});
  const [error, setError] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await Http.get(`/story`);
      console.log("Stories data:", res.data.data);
      setStories(res.data.data || []);

      // Fetch related data for displaying names instead of IDs
      await Promise.allSettled([fetchProjects(), fetchLabels(), fetchDevelopers()]);
    } catch (error) {
      console.error("Error fetching stories:", error);
      setError("Failed to load stories. Please refresh the page to try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await Http.get(`/project`);
      const projectsMap = {};
      if (res.data && res.data.data) {
        res.data.data.forEach((project) => {
          projectsMap[project.id] = project.title;
        });
      }
      setProjects(projectsMap);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const fetchLabels = async () => {
    try {
      const res = await Http.get(`/label`);
      // console.log("Labels data:", res.data.data);
      const labelsMap = {};
      if (res.data && res.data.data) {
        res.data.data.forEach((label) => {
          labelsMap[label.id] = {
            name: label.name,
            color: label.color,
          };
        });
      }
      setLabels(labelsMap);
    } catch (error) {
      console.error("Error fetching labels:", error);
    }
  };

  const fetchDevelopers = async () => {
    try {
      // Use the correct endpoint for staff/developers
      const res = await Http.get(`/staff-department-wise/30`);
      // console.log("Developers data:", res.data.data);
      const developersMap = {};
      if (res.data && res.data.data) {
        res.data.data.forEach((dev) => {
          // Handle different possible name formats
          const fullName = `${dev.first_name || ""} ${dev.last_name || ""}`.trim();
          developersMap[dev.id] = fullName || dev.name || `ID: ${dev.id}`;
        });
      }
      setDevelopers(developersMap);
    } catch (error) {
      console.error("Error fetching developers:", error);
      // Continue even if developers can't be fetched - we'll display "Unassigned" in the table
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const deleteData = useDeleteData(fetchData);

  const handleEdit = (story) => {
    setCurrentStory(story);
    setEditVisible(true);
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Filter stories based on search term
  const filteredStories = stories.filter(
    (story) =>
      story.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (projects[story.project_id] && projects[story.project_id].toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Function to render label with color
  const renderLabel = (labelId) => {
    if (!labelId || !labels[labelId]) return "N/A";
    const label = labels[labelId];

    return (
      <span
        className="badge"
        style={{
          backgroundColor: label.color || "#ccc",
          color: "#fff",
          padding: "6px 10px",
          borderRadius: "4px",
          fontSize: "12px",
        }}
      >
        {label.name}
      </span>
    );
  };

  // Function to render status with appropriate color
  const renderStatus = (status) => {
    let color = "";
    switch (status) {
      case "To Do":
        color = "#6c757d"; // gray
        break;
      case "In Progress":
        color = "#0d6efd"; // blue
        break;
      case "Review":
        color = "#fd7e14"; // orange
        break;
      case "Done":
        color = "#198754"; // green
        break;
      default:
        color = "#6c757d"; // default gray
    }

    return (
      <span
        className="badge"
        style={{
          backgroundColor: color,
          color: "#fff",
          padding: "6px 10px",
          borderRadius: "4px",
          fontSize: "12px",
        }}
      >
        {status}
      </span>
    );
  };

  return (
    <div>
      <IsLogin />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Stories</h5>
          <div className="d-flex justify-content-between align-items-center w-100 mt-2">
            <div className="d-flex align-items-center">
              <span className="p-input-icon-left mr-3" style={{ width: "300px" }}>
                <i className="pi pi-search" style={{ marginLeft: "4px" }} />
                <InputText
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search stories..."
                  className="w-100"
                  style={{ paddingLeft: "20px" }}
                />
              </span>
            </div>
            <div>
              <Button label="Create Story" icon="pi pi-plus" className="p-button-success" onClick={() => setVisible(true)} />
            </div>
          </div>
        </div>

        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {loading ? (
              <div className="text-center p-5">
                <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
                <p className="mt-2">Loading stories...</p>
              </div>
            ) : filteredStories.length === 0 ? (
              <div className="text-center p-5">
                <i className="pi pi-folder-open" style={{ fontSize: "3rem", color: "#ccc" }}></i>
                <p className="mt-2">No stories found. Create your first story!</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mg-b-0">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Project</th>
                      <th>Status</th>
                      <th>Developer</th>
                      <th>Story Points</th>
                      <th>Label</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStories.map((story) => (
                      <tr key={story.id}>
                        {/* Story Title */}
                        <td>
                          <div className="story-title" style={{ maxWidth: "250px" }}>
                            <strong>{story.title}</strong>
                            {story.description && (
                              <p className="text-muted mb-0 text-truncate">
                                {story.description.replace(/<[^>]*>/g, "").substring(0, 100)}
                                {story.description.length > 100 ? "..." : ""}
                              </p>
                            )}
                          </div>
                        </td>

                        {/* Project */}
                        <td>{projects[story.project_id] || "N/A"}</td>

                        {/* Status */}
                        <td>{renderStatus(story.status)}</td>

                        {/* Developer */}
                        <td>{story.developer ? story.developer.name : "Unassigned"}</td>

                        {/* Story Points */}
                        <td>
                          <span className="badge bg-primary">{story.story_points || 0}</span>
                        </td>

                        {/* Label */}
                        <td>{renderLabel(story.label_id)}</td>

                        {/* Created */}
                        <td>
                          <small className="text-muted">{formatDate(story.created_at)}</small>
                        </td>

                        {/* Actions */}
                        <td>
                          <div className="d-flex">
                            <Button
                              icon="pi pi-pencil"
                              className="p-button-warning p-button-sm mr-2"
                              onClick={() => handleEdit(story)}
                              tooltip="Edit"
                            />
                            <Button
                              icon="pi pi-trash"
                              className="p-button-danger p-button-sm"
                              onClick={() => deleteData(story.id, "story", "story")}
                              tooltip="Delete"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog header="Create Story" visible={visible} style={{ width: "50vw" }} onHide={() => setVisible(false)} dismissableMask>
        <Create setVisible={setVisible} fetchData={fetchData} />
      </Dialog>

      {/* Edit Dialog */}
      <Dialog header="Edit Story" visible={editVisible} style={{ width: "50vw" }} onHide={() => setEditVisible(false)} dismissableMask>
        <Edit setVisible={setEditVisible} fetchData={fetchData} rowData={currentStory} />
      </Dialog>
    </div>
  );
};

export default List;
